import React, { useContext, useState, useEffect, useMemo } from "react";
import Sidebar from "../Sidebar/Sidebar";
import Header from "../Header/Header";
import { Line, Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, LineElement, PointElement, Filler } from "chart.js";
import Cookies from "js-cookie";
import { 
  FaCalendarAlt, 
  FaRegCalendarCheck, 
  FaUser, 
  FaArrowUp, 
  FaArrowDown,
  FaChartLine,
  FaChartBar,
  FaInfoCircle,
  FaSyncAlt,
  FaExclamationTriangle
} from "react-icons/fa";
import { HiOutlineRefresh, HiOutlineOfficeBuilding } from "react-icons/hi";
import AdminDetails from "../context/adminDetailsContext";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, LineElement, PointElement, Filler);

const Home = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const [selectedGraph, setSelectedGraph] = useState("users");
  const [userFrequency, setUserFrequency] = useState("monthly");
  const [eventFrequency, setEventFrequency] = useState("monthly");
  const [articleFrequency, setArticleFrequency] = useState("monthly");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const { details } = useContext(AdminDetails);
  const [profileName , setProfileName] = useState("");
  
  // State for stats
  const [stats, setStats] = useState({
    users: { total: 0, percentage: 0, trend: 'up', previousTotal: 0 },
    events: { total: 0, percentage: 0, trend: 'up', previousTotal: 0 },
    articles: { total: 0, percentage: 0, trend: 'up', previousTotal: 0 }
  });

  // State for chart data
  const [chartData, setChartData] = useState({
    users: { monthly: { labels: [], data: [] }, yearly: { labels: [], data: [] } },
    events: { monthly: { labels: [], data: [] }, yearly: { labels: [], data: [] } },
    articles: { monthly: { labels: [], data: [] }, yearly: { labels: [], data: [] } }
  });

  const [loading, setLoading] = useState({
    users: true,
    events: true,
    articles: true,
    initial: true
  });

  const authToken = Cookies.get("authToken");
  const collegeName = details?.admin?.collegeName || "Dashboard";

  // Calculate summary stats
  const summary = useMemo(() => {
    const total = stats.users.total + stats.events.total + stats.articles.total;
    const active = (stats.users.total > 0 ? 1 : 0) + 
                   (stats.events.total > 0 ? 1 : 0) + 
                   (stats.articles.total > 0 ? 1 : 0);
    return { total, active };
  }, [stats]);

  const fetchUsersData = async (showToast = false) => {
    try {
      setLoading(prev => ({ ...prev, users: true }));
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/apis/admin/getAllUser`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
      }
      
      if (responseData.success && responseData.data) {
        const data = responseData.data;
        
        // Update stats
        const percentage = parseFloat(data.stats?.percentageChange || 0);
        setStats(prev => ({
          ...prev,
          users: {
            total: data.totalUsers || 0,
            percentage: Math.abs(percentage),
            trend: percentage >= 0 ? 'up' : 'down',
            previousTotal: prev.users.total
          }
        }));

        // Update chart data
        setChartData(prev => ({
          ...prev,
          users: {
            monthly: {
              labels: data.charts?.monthly?.labels || ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
              data: data.charts?.monthly?.data || Array(12).fill(0)
            },
            yearly: {
              labels: data.charts?.yearly?.labels?.length ? data.charts.yearly.labels : ["2024"],
              data: data.charts?.yearly?.data?.length ? data.charts.yearly.data : [0]
            }
          }
        }));

        if (showToast) {
          toast.success(`📊 Users data updated`, { autoClose: 2000 });
        }
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setError(prev => ({ ...prev, users: error.message }));
      if (showToast) {
        toast.error("Failed to load users data");
      }
    } finally {
      setLoading(prev => ({ ...prev, users: false }));
    }
  };

    // Fetch profile data
    useEffect(() => {
      const fetchProfileData = async () => {
        if (!authToken) {
          setLoading(false);
          return;
        }
        
        try {
          setLoading(true);
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/apis/admin/getAdminProfile`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${authToken}`,
              },
            }
          );
  
          if (!response.ok)
            throw new Error(`HTTP error! status: ${response.status}`);
  
          const data = await response.json();
          
          if (data?.data) {
            const adminData = data.data;
            setProfileName(adminData.fullName || "Admin User");
          }
        } catch (err) {
          console.error("Error fetching profile:", err);
          // Don't show error toast for profile fetch failure
        } finally {
          setLoading(false);
        }
      };
  
      fetchProfileData();
    }, [authToken]);

  const fetchEventsData = async (showToast = false) => {
    try {
      setLoading(prev => ({ ...prev, events: true }));
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/apis/admin/getAllEvent`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
      }
      
      if (responseData.success) {
        const eventsData = responseData.message;
        
        const percentage = parseFloat(eventsData?.stats?.percentageChange || 0);
        setStats(prev => ({
          ...prev,
          events: {
            total: eventsData?.totalEvents || 0,
            percentage: Math.abs(percentage),
            trend: percentage >= 0 ? 'up' : 'down',
            previousTotal: prev.events.total
          }
        }));

        setChartData(prev => ({
          ...prev,
          events: {
            monthly: {
              labels: eventsData?.charts?.monthly?.labels || ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
              data: eventsData?.charts?.monthly?.data || Array(12).fill(0)
            },
            yearly: {
              labels: eventsData?.charts?.yearly?.labels?.length ? eventsData.charts.yearly.labels : ["2024"],
              data: eventsData?.charts?.yearly?.data?.length ? eventsData.charts.yearly.data : [0]
            }
          }
        }));

        if (showToast) {
          toast.success(`📅 Events data updated`, { autoClose: 2000 });
        }
      }
    } catch (error) {
      console.error("Error fetching events:", error);
      setError(prev => ({ ...prev, events: error.message }));
      if (showToast) {
        toast.error("Failed to load events data");
      }
    } finally {
      setLoading(prev => ({ ...prev, events: false }));
    }
  };

  const fetchArticlesData = async (showToast = false) => {
    try {
      setLoading(prev => ({ ...prev, articles: true }));
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/apis/admin/getAllBlog`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
      }
      
      if (responseData.success && responseData.data) {
        const data = responseData.data;
        
        const percentage = parseFloat(data.stats?.percentageChange || 0);
        setStats(prev => ({
          ...prev,
          articles: {
            total: data.totalBlogs || 0,
            percentage: Math.abs(percentage),
            trend: percentage >= 0 ? 'up' : 'down',
            previousTotal: prev.articles.total
          }
        }));

        setChartData(prev => ({
          ...prev,
          articles: {
            monthly: {
              labels: data.charts?.monthly?.labels || ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
              data: data.charts?.monthly?.data || Array(12).fill(0)
            },
            yearly: {
              labels: data.charts?.yearly?.labels?.length ? data.charts.yearly.labels : ["2024"],
              data: data.charts?.yearly?.data?.length ? data.charts.yearly.data : [0]
            }
          }
        }));

        if (showToast) {
          toast.success(`📝 Articles data updated`, { autoClose: 2000 });
        }
      }
    } catch (error) {
      console.error("Error fetching articles:", error);
      setError(prev => ({ ...prev, articles: error.message }));
      if (showToast) {
        toast.error("Failed to load articles data");
      }
    } finally {
      setLoading(prev => ({ ...prev, articles: false }));
    }
  };

  // Initial fetch
  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(prev => ({ ...prev, initial: true }));
      setError(null);
      
      await Promise.allSettled([
        fetchUsersData(),
        fetchEventsData(),
        fetchArticlesData()
      ]);
      
      setLastUpdated(new Date());
      setLoading(prev => ({ ...prev, initial: false }));
    };

    if (authToken) {
      fetchAllData();
    }
  }, [authToken]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.allSettled([
      fetchUsersData(true),
      fetchEventsData(true),
      fetchArticlesData(true)
    ]);
    setLastUpdated(new Date());
    setRefreshing(false);
    toast.success("✨ Dashboard updated successfully!");
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          boxWidth: 10,
          font: {
            size: window.innerWidth < 768 ? 10 : 12
          },
          color: '#4B5563'
        }
      },
      tooltip: {
        backgroundColor: '#1F2937',
        titleColor: '#F9FAFB',
        bodyColor: '#D1D5DB',
        borderColor: '#374151',
        borderWidth: 1,
        padding: 10,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toLocaleString();
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          font: { size: window.innerWidth < 768 ? 8 : 10 },
          color: '#6B7280'
        }
      },
      y: {
        grid: {
          display: true,
          color: '#E5E7EB',
          drawBorder: false
        },
        ticks: {
          font: { size: window.innerWidth < 768 ? 8 : 10 },
          color: '#6B7280',
          callback: function(value) {
            return value.toLocaleString();
          }
        },
        beginAtZero: true
      }
    },
    animation: {
      duration: 750,
      easing: 'easeInOutQuart'
    },
    interaction: {
      mode: 'index',
      intersect: false
    }
  };

  const getUserChartData = () => {
    const data = userFrequency === "monthly" 
      ? chartData.users.monthly.data
      : chartData.users.yearly.data;
    
    const labels = userFrequency === "monthly"
      ? chartData.users.monthly.labels
      : chartData.users.yearly.labels;

    return {
      labels,
      datasets: [
        {
          label: "User Growth",
          data: data,
          backgroundColor: "rgba(156, 163, 175, 0.2)",
          borderColor: "rgba(75, 85, 99, 1)",
          borderWidth: 2,
          borderRadius: 4,
          barPercentage: 0.7,
          categoryPercentage: 0.8
        },
      ],
    };
  };

  const getEventChartData = () => {
    const data = eventFrequency === "monthly" 
      ? chartData.events.monthly.data
      : chartData.events.yearly.data;
    
    const labels = eventFrequency === "monthly"
      ? chartData.events.monthly.labels
      : chartData.events.yearly.labels;

    return {
      labels,
      datasets: [
        {
          label: "Events",
          data: data,
          fill: false,
          borderColor: "rgba(75, 85, 99, 1)",
          backgroundColor: "rgba(75, 85, 99, 0.1)",
          tension: 0.1,
          pointBackgroundColor: "rgba(75, 85, 99, 1)",
          pointBorderColor: "#fff",
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6
        },
      ],
    };
  };

  const getArticleChartData = () => {
    const data = articleFrequency === "monthly" 
      ? chartData.articles.monthly.data
      : chartData.articles.yearly.data;
    
    const labels = articleFrequency === "monthly"
      ? chartData.articles.monthly.labels
      : chartData.articles.yearly.labels;

    return {
      labels,
      datasets: [
        {
          label: "Articles",
          data: data,
          backgroundColor: "rgba(156, 163, 175, 0.2)",
          borderColor: "rgba(75, 85, 99, 1)",
          borderWidth: 2,
          borderRadius: 4,
          barPercentage: 0.7,
          categoryPercentage: 0.8
        },
      ],
    };
  };

  const StatCardSkeleton = () => (
    <div className="bg-white p-4 sm:p-5 md:p-6 rounded-lg sm:rounded-xl shadow-md border border-gray-200 animate-pulse">
      <div className="flex justify-between items-center">
        <div className="flex-1">
          <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
          <div className="h-8 bg-gray-300 rounded w-20 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-28"></div>
        </div>
        <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
      </div>
    </div>
  );

  const getIcon = (type) => {
    switch(type) {
      case 'users': return FaUser;
      case 'events': return FaCalendarAlt;
      case 'articles': return FaRegCalendarCheck;
      default: return FaUser;
    }
  };

  const StatCard = ({ type, data, loading, onClick }) => {
    const Icon = getIcon(type);
    const hasData = data.total > 0;
    
    return (
      <motion.div
        whileHover={{ y: -4 }}
        whileTap={{ scale: 0.98 }}
        className={`bg-white p-4 sm:p-5 md:p-6 rounded-lg sm:rounded-xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border ${
          selectedGraph === type ? 'border-gray-600 ring-2 ring-gray-400 ring-opacity-50' : 'border-gray-200'
        }`}
        onClick={onClick}
        role="button"
        tabIndex={0}
        aria-label={`View ${type} statistics`}
        onKeyPress={(e) => e.key === 'Enter' && onClick()}
      >
        <div className="flex justify-between items-center">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-600 truncate capitalize">
              Total {type}
            </h3>
            {loading ? (
              <div className="h-8 bg-gray-200 rounded w-20 mt-2 animate-pulse"></div>
            ) : (
              <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mt-1 sm:mt-2">
                {data.total.toLocaleString()}
              </p>
            )}
            
            {!loading && hasData && (
              <motion.div 
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center mt-1"
              >
                {data.trend === 'up' ? (
                  <FaArrowUp className="text-green-500 text-xs sm:text-sm mr-1" />
                ) : (
                  <FaArrowDown className="text-red-500 text-xs sm:text-sm mr-1" />
                )}
                <p className={`text-xs sm:text-sm ${data.trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                  {data.percentage}% from last month
                </p>
              </motion.div>
            )}
            
            {!loading && !hasData && (
              <p className="text-xs sm:text-sm text-gray-500 mt-1">No data available</p>
            )}
          </div>
          
          <div className={`p-2 sm:p-3 rounded-lg ml-2 flex-shrink-0 border transition-colors ${
            selectedGraph === type 
              ? 'bg-gray-700 border-gray-600' 
              : 'bg-gray-100 border-gray-200'
          }`}>
            <Icon className={`text-xl sm:text-2xl md:text-3xl transition-colors ${
              selectedGraph === type ? 'text-white' : 'text-gray-600'
            }`} />
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 w-full">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} activePage="/home" />
      
      <div className="flex-1 w-0">
        <Header toggleSidebar={toggleSidebar} />
        
        <main className="p-3 sm:p-4 md:p-6 overflow-auto">
          {/* Welcome Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
          >
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
                Welcome back, {details?.admin?.adminName || 'Admin'}! 👋
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {collegeName} • {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {lastUpdated && (
                <div className="text-xs text-gray-400 flex items-center gap-1">
                  <FaInfoCircle className="w-3 h-3" />
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </div>
              )}
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                aria-label="Refresh dashboard"
              >
                <HiOutlineRefresh className={`w-5 h-5 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
              </motion.button>
            </div>
          </motion.div>

          {/* Summary Card */}
          {!loading.initial && summary.total > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 bg-gradient-to-r from-gray-700 to-gray-900 text-white rounded-xl p-4 shadow-lg"
            >
              <div className="flex items-center gap-3">
                <HiOutlineOfficeBuilding className="w-6 h-6 opacity-80" />
                <div>
                  <p className="text-sm opacity-90">Total Activity</p>
                  <p className="text-2xl font-bold">{summary.total} items</p>
                </div>
                <div className="ml-auto text-right">
                  <p className="text-sm opacity-90">Active Sections</p>
                  <p className="text-xl font-semibold">{summary.active}/3</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-6 md:mb-8">
            {loading.initial ? (
              <>
                <StatCardSkeleton />
                <StatCardSkeleton />
                <StatCardSkeleton />
              </>
            ) : (
              <>
                <StatCard
                  type="users"
                  data={stats.users}
                  loading={loading.users}
                  onClick={() => setSelectedGraph("users")}
                />
                <StatCard
                  type="events"
                  data={stats.events}
                  loading={loading.events}
                  onClick={() => setSelectedGraph("events")}
                />
                <StatCard
                  type="articles"
                  data={stats.articles}
                  loading={loading.articles}
                  onClick={() => setSelectedGraph("article")}
                />
              </>
            )}
          </div>

          {/* Chart Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl shadow-md border border-gray-200"
          >
            {selectedGraph && (
              <>
                <div className="flex flex-col xs:flex-row items-start xs:items-center justify-between gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    {selectedGraph === "users" && <FaChartBar className="text-gray-600" />}
                    {selectedGraph === "events" && <FaChartLine className="text-gray-600" />}
                    {selectedGraph === "article" && <FaChartBar className="text-gray-600" />}
                    <h3 className="text-sm sm:text-base font-semibold text-gray-700 capitalize">
                      {selectedGraph === "article" ? "Articles" : selectedGraph} Overview
                    </h3>
                  </div>
                  
                  <div className="flex items-center gap-3 w-full xs:w-auto">
                    <label className="text-xs sm:text-sm font-medium text-gray-600">Frequency:</label>
                    <select
                      className="flex-1 xs:flex-none border border-gray-300 rounded-lg px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white"
                      value={
                        selectedGraph === "users" ? userFrequency : 
                        selectedGraph === "events" ? eventFrequency : 
                        articleFrequency
                      }
                      onChange={(e) => {
                        if (selectedGraph === "users") setUserFrequency(e.target.value);
                        else if (selectedGraph === "events") setEventFrequency(e.target.value);
                        else setArticleFrequency(e.target.value);
                      }}
                      aria-label="Select chart frequency"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                </div>

                <div className="relative h-64 xs:h-72 sm:h-80 md:h-96 w-full">
                  {loading[selectedGraph === "article" ? "articles" : selectedGraph] ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80">
                      <div className="flex flex-col items-center gap-2">
                        <div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-gray-600"></div>
                        <p className="text-sm text-gray-500">Loading chart data...</p>
                      </div>
                    </div>
                  ) : (
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={`${selectedGraph}-${userFrequency}-${eventFrequency}-${articleFrequency}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="h-full w-full"
                      >
                        {selectedGraph === "users" && (
                          <Bar data={getUserChartData()} options={chartOptions} />
                        )}
                        {selectedGraph === "events" && (
                          <Line data={getEventChartData()} options={chartOptions} />
                        )}
                        {selectedGraph === "article" && (
                          <Bar data={getArticleChartData()} options={chartOptions} />
                        )}
                      </motion.div>
                    </AnimatePresence>
                  )}
                </div>

                {/* Chart Legend/Info */}
                <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center text-xs text-gray-400">
                  <span>
                    Showing {selectedGraph === "users" ? userFrequency : 
                             selectedGraph === "events" ? eventFrequency : 
                             articleFrequency} data
                  </span>
                  <span className="flex items-center gap-1">
                    <FaInfoCircle className="w-3 h-3" />
                    Click on cards above to switch charts
                  </span>
                </div>
              </>
            )}
          </motion.div>

          {/* Error Display */}
          <AnimatePresence>
            {error && Object.keys(error).length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg"
              >
                <div className="flex items-start gap-3">
                  <FaExclamationTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-red-800">Some data couldn't be loaded</h4>
                    <p className="text-sm text-red-600 mt-1">
                      {Object.values(error).filter(Boolean).join(', ')}
                    </p>
                    <button
                      onClick={handleRefresh}
                      className="mt-2 text-sm text-red-700 hover:text-red-800 font-medium underline"
                    >
                      Try refreshing
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

export default Home;